/**
 * useSlideThinkingSync - Bridges slideConversations thinking to message store
 *
 * When viewing a generating child slide conversation in ChatArea,
 * this hook syncs the thinking from videoWorkspaceStore.slideConversations
 * to the message's currentThinkingSubject so the existing ThinkingIndicator
 * in MessageBubble displays it.
 *
 * This avoids duplicating the thinking indicator UI and keeps the system unified.
 */

import { useEffect, useRef } from 'react';
import { useVideoWorkspaceStore } from '@/stores/videoWorkspaceStore';
import { useMessageStore } from '@/stores/messageStore';

export function useSlideThinkingSync(conversationId: string | null) {
  const { slideConversations } = useVideoWorkspaceStore();
  const messageStore = useMessageStore();

  // Track the last message we updated to avoid unnecessary updates
  const lastUpdatedMessageRef = useRef<string | null>(null);
  const lastThinkingRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!conversationId) return;

    // Find if this conversation is a generating child slide
    const slideConv = slideConversations.find(sc => sc.id === conversationId);

    if (!slideConv) {
      // Not a child slide conversation, nothing to sync
      return;
    }

    const messages = messageStore.getMessagesForConversation(conversationId);
    const lastAssistantMsg = [...messages].reverse().find(m => m.role === 'assistant');

    if (slideConv.status === 'generating') {
      // Slide is generating - sync thinking to last assistant message
      // Use generatingThinking if available, otherwise show default "Processing..."
      const thinkingToShow = slideConv.generatingThinking || 'Processing...';

      if (lastAssistantMsg) {
        // Only update if thinking has changed to avoid infinite loops
        if (
          lastUpdatedMessageRef.current !== lastAssistantMsg.id ||
          lastThinkingRef.current !== thinkingToShow
        ) {
          console.log('💭 SlideThinkingSync: Syncing thinking to message', {
            conversationId,
            messageId: lastAssistantMsg.id,
            outlineSlideId: slideConv.outlineSlideId,
            thinking: thinkingToShow?.substring(0, 50),
          });

          messageStore.updateMessage(lastAssistantMsg.id, {
            currentThinkingSubject: thinkingToShow,
            status: 'streaming', // Ensure streaming state for UI
          });

          lastUpdatedMessageRef.current = lastAssistantMsg.id;
          lastThinkingRef.current = thinkingToShow;
        }
      } else if (messages.length > 0) {
        // No assistant message yet, but we have messages
        // The message might still be loading from API
        console.log('💭 SlideThinkingSync: Waiting for assistant message', {
          conversationId,
          messageCount: messages.length,
        });
      }
    } else if (slideConv.status === 'completed') {
      // Generation completed - clear thinking from message

      if (lastAssistantMsg?.currentThinkingSubject) {
        console.log('💭 SlideThinkingSync: Clearing thinking (generation completed)', {
          conversationId,
          messageId: lastAssistantMsg.id,
        });

        messageStore.updateMessage(lastAssistantMsg.id, {
          currentThinkingSubject: undefined,
          status: 'completed',
        });

        lastUpdatedMessageRef.current = null;
        lastThinkingRef.current = undefined;
      }
    } else if (slideConv.status === 'failed') {
      // Generation failed - clear thinking

      if (lastAssistantMsg?.currentThinkingSubject) {
        console.log('💭 SlideThinkingSync: Clearing thinking (generation failed)', {
          conversationId,
          messageId: lastAssistantMsg.id,
        });

        messageStore.updateMessage(lastAssistantMsg.id, {
          currentThinkingSubject: undefined,
          status: 'error',
        });

        lastUpdatedMessageRef.current = null;
        lastThinkingRef.current = undefined;
      }
    }
  }, [conversationId, slideConversations, messageStore]);

  // Cleanup when conversation changes
  useEffect(() => {
    return () => {
      lastUpdatedMessageRef.current = null;
      lastThinkingRef.current = undefined;
    };
  }, [conversationId]);
}

export default useSlideThinkingSync;
